import { Injectable } from '@angular/core';
import { EcommerceTrackingMapperService } from '@epicuro-next/addons/ecommerce-tracking-mappers';
import { ReferencedProduct } from '@epicuro-next/data/baskets-dto';
import { PricesService } from '@epicuro-next/data/prices';
import { IUser } from '@epicuro-next/data/users';
import { selectGcrNormalizePriceForPurchaseEvent } from '@epicuro-next/platform/configuration';
import {
  EcommerceActionsToTrack,
  IGtmProperties,
} from '@epicuro-next/platform/tracking';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import {
  addItemsToBasketTracking,
  clearBasketFailure,
  clearBasketSuccess,
  expandDefaultBasketLines,
  ExtendedBasketItem,
  groupDtoBasketFailure,
  groupDtoBasketSuccess,
  loadDtoBasketSuccess,
  processNewItemsPrepack,
  processNewItemsSize,
  processOldItemsPrepack,
  processOldItemsSize,
  selectActiveBasketCurrency,
  selectAllBasketItemsTracking,
  selectBasketLoading,
  selectExtendedBasketItems,
  selectOperableBasketData,
  selectOperableExtendedBasketItems,
  selectOperableReferencedProducts,
  selectReferencedProducts,
  updateBasketItemsFailure,
  updateBasketShopsFail,
  updateBasketShopsSuccess,
  updateDtoBasketSuccess,
} from '@epicuro-next/state/basket-state';
import {
  importMDOBasketFailure,
  importMDOBasketSuccess,
  uploadSDOBasketFailure,
  uploadSDOBasketSuccess,
} from '@epicuro-next/state/basket-upload-state';
import { selectProductSource } from '@epicuro-next/state/config-strip-state';
import { selectListEntities } from '@epicuro-next/state/list-entities';
import { selectLookbookEntityForTracking } from '@epicuro-next/state/lookbook-entities';
import { selectSessionState } from '@epicuro-next/state/session-state';
import { selectCurrentSite } from '@epicuro-next/state/site-state';
import { gtmTrackActions } from '@epicuro-next/state/tracking';
import {
  updateBasketError,
  updateBasketSuccess,
} from '@epicuro-next/state/ubc-communication/ubc-basket';
import { selectUser } from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { RouterNavigatedAction, ROUTER_NAVIGATED } from '@ngrx/router-store';
import { select, Store } from '@ngrx/store';
import { Basket, Maybe } from 'graphql-api/baskets-api-interfaces';
import { Account } from 'graphql-api/gql-api-interfaces';
import { of, race } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import { defaultCollectorSelect } from '../shared/default-collector-select';
import { mapProductSource } from '../shared/map-product-source';

@Injectable()
export class BasketDataCollectorsEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private ecommerceTrackingMapper: EcommerceTrackingMapperService,
    private pricesService: PricesService,
  ) {}

  public sendPurchaseEventOnsubmitBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.SubmitBasketSuccess),
      concatLatestFrom(() => [
        this.store.select(selectExtendedBasketItems),
        this.store.select(selectActiveBasketCurrency),
        this.store.select(selectOperableBasketData),
        this.store.select(selectUser),
        this.store.select(selectActiveAccount),
        this.store.select(selectSessionState),
        this.store.select(selectReferencedProducts),
        this.store.select(selectAllBasketItemsTracking),
        this.store.select(selectGcrNormalizePriceForPurchaseEvent),
        ...defaultCollectorSelect(this.store),
      ]),
      concatMap(
        ([
          { eventMappingFunction, payload },
          basketItems,
          currency,
          basket,
          user,
          account,
          session,
          referencedProducts,
          basketItemsTracking,
          gcrNormalizePriceForPurchaseEvent,
          activeAccount,
          presetDisplayName,
        ]) => {
          const extendedItems = new Map();

          let normalizedPrice = 0;

          processNewItemsSize(basketItems, [], extendedItems);
          processNewItemsPrepack(
            basketItems,
            [],
            referencedProducts,
            extendedItems,
          );

          const mapper = this.ecommerceTrackingMapper
            .mapEventsFromAction({}, eventMappingFunction)
            .pipe(
              mergeMap((eventNames) =>
                eventNames.map((eventName) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name: eventName,
                    payload: {
                      ...payload,
                      currency,
                      basket,
                      user,
                      account,
                      session,
                      referencedProducts,
                      extendedItems: [...extendedItems.values()],
                      basketItemsTracking,
                      activeAccount,
                      presetDisplayName,
                      normalizedPrice,
                    },
                  }),
                ),
              ),
            );

          if (gcrNormalizePriceForPurchaseEvent) {
            const total =
              basket?.total.requested.net ?? basket?.total.requested.wsp ?? 0;
            return this.pricesService
              .convert(currency ?? '', 'USD', total)
              .pipe(
                switchMap((converted) => {
                  normalizedPrice = parseFloat(
                    converted.resultedPrice.toFixed(2),
                  );
                  return mapper;
                }),
                catchError((e) => {
                  console.error(
                    `Couldn't convert ${currency} to USD of value: ${e}`,
                  );
                  return of(e);
                }),
              );
          }
          return mapper;
        },
      ),
    ),
  );

  private readonly updateBasketItemsWaiter$ = race(
    this.actions$.pipe(
      ofType(
        importMDOBasketSuccess,
        uploadSDOBasketSuccess,
        updateDtoBasketSuccess,
        updateBasketSuccess,
        updateBasketShopsSuccess,
      ),
      take(1),
      takeUntil(
        this.actions$.pipe(
          ofType(
            importMDOBasketFailure,
            uploadSDOBasketFailure,
            updateBasketItemsFailure,
            updateBasketError,
            updateBasketShopsFail,
          ),
        ),
      ),
    ),
    // should wait load basket as clear basket discount triggers groupDtoBasketSuccess before basket updated
    this.actions$.pipe(
      ofType(clearBasketSuccess),
      take(1),
      switchMap(() =>
        this.actions$.pipe(ofType(loadDtoBasketSuccess), take(1)),
      ),
      takeUntil(this.actions$.pipe(ofType(clearBasketFailure))),
    ),
  ).pipe(
    switchMap(() => this.actions$.pipe(ofType(groupDtoBasketSuccess), take(1))),
  );

  public sendBasketContentOrAddOrRemoveFromCartEventOnUpdatedBasketItems$ =
    createEffect(() =>
      this.actions$.pipe(
        ofType(gtmTrackActions.gtmCollectTrackData),
        filter((x) => x.name === EcommerceActionsToTrack.UpdateBasketItems),
        withLatestFrom(
          this.store.select(selectOperableExtendedBasketItems),
          this.store.select(selectOperableReferencedProducts),
          this.store.select(selectActiveBasketCurrency),
        ),
        map(([action, oldItems, oldReferencedProducts, oldCurrency]) => ({
          action,
          oldItems,
          oldReferencedProducts,
          oldCurrency,
        })),
        switchMap((basket) => {
          return this.updateBasketItemsWaiter$.pipe(map(() => basket));
        }),
        concatLatestFrom(() => [
          this.store.select(selectOperableExtendedBasketItems),
          this.store.select(selectActiveBasketCurrency),
          this.store.select(selectOperableReferencedProducts),
          this.store.select(selectOperableBasketData),
          this.store.select(selectProductSource),
          this.store.select(selectLookbookEntityForTracking),
          this.store.select(selectListEntities),
          ...defaultCollectorSelect(this.store),
          this.store.select(selectCurrentSite),
        ]),
        concatMap(
          ([
            { action, oldItems, oldReferencedProducts, oldCurrency },
            newItems,
            currency,
            referencedProducts,
            basket,
            productSource,
            lookbookHashMap,
            listEntities,
            activeAccount,
            presetDisplayName,
            ,
            user,
            currentSite,
          ]) => {
            const addedItems = new Map<string, ExtendedBasketItem>();

            processNewItemsSize(newItems, oldItems, addedItems);
            processNewItemsPrepack(
              newItems,
              oldItems,
              referencedProducts,
              addedItems,
            );

            const basketContent = new Map<string, ExtendedBasketItem>();

            processNewItemsSize(newItems, [], basketContent);
            processNewItemsPrepack(
              newItems,
              [],
              referencedProducts,
              basketContent,
            );

            const removedItems = new Map<string, ExtendedBasketItem>();

            processOldItemsSize(oldItems, newItems, removedItems);
            processOldItemsPrepack(
              oldItems,
              newItems,
              oldReferencedProducts,
              removedItems,
            );

            const gtmProductSource = mapProductSource(
              productSource,
              lookbookHashMap,
              listEntities,
            );

            const payload = {
              addedItems: [...addedItems.values()],
              removedItems: [...removedItems.values()],
              basketContent: [...basketContent.values()],
              currency: currency?.length ? currency : oldCurrency,
              basket,
              referencedProducts,
              oldReferencedProducts,
              productSource,
              mappedProductSource: gtmProductSource,
              activeAccount,
              presetDisplayName,
              user,
              currentSite,
            };

            this.store.dispatch(
              addItemsToBasketTracking({
                addedItems: [...addedItems.values()],
                productSource,
                mappedProductSource: gtmProductSource,
              }),
            );
            return this.ecommerceTrackingMapper
              .mapEventsFromAction(payload, action.eventMappingFunction)
              .pipe(
                mergeMap((eventNames) => {
                  return eventNames.map((eventName) =>
                    gtmTrackActions.gtmSendTrackEvent({
                      name: eventName,
                      payload,
                    }),
                  );
                }),
              );
          },
        ),
      ),
    );

  public sendViewCartEventOnShowingCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.ViewCart),
      switchMap((action) => {
        return race(
          this.actions$.pipe(ofType(expandDefaultBasketLines)),
          this.actions$.pipe(
            ofType(ROUTER_NAVIGATED),
            filter((a: RouterNavigatedAction) => {
              return (
                a.payload.event.urlAfterRedirects.startsWith('/basket') &&
                !a.payload.event.urlAfterRedirects.includes('overlay:cs')
              );
            }),
            switchMap(() => this.store.pipe(select(selectBasketLoading))),
            filter((value) => !value),
          ),
        ).pipe(
          take(1),
          map(() => action),
        );
      }),
      concatLatestFrom(() => [
        this.store.select(selectOperableExtendedBasketItems),
        this.store.select(selectActiveBasketCurrency),
        this.store.select(selectOperableBasketData),
        this.store.select(selectOperableReferencedProducts),
        ...defaultCollectorSelect(this.store),
      ]),
      mergeMap(
        ([
          { eventMappingFunction, payload },
          basketItems,
          currency,
          basketData,
          referencedProducts,
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) =>
          this.mapToViewCartEvent([
            { eventMappingFunction, payload },
            basketItems,
            currency,
            basketData,
            referencedProducts,
            activeAccount as Account,
            presetDisplayName as string,
            user as IUser,
          ]),
      ),
    ),
  );

  public sendViewCartEventOnUpdateShowingCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.ViewCart),
      switchMap((action) =>
        this.actions$.pipe(
          ofType(updateDtoBasketSuccess),
          takeUntil(this.actions$.pipe(ofType(updateBasketItemsFailure))),
          take(1),
          map(() => action),
        ),
      ),
      switchMap((action) =>
        this.actions$.pipe(
          ofType(groupDtoBasketSuccess),
          takeUntil(this.actions$.pipe(ofType(groupDtoBasketFailure))),
          take(1),
          map(() => action),
        ),
      ),
      filter(() => window.location.href.includes('basket')),
      concatLatestFrom(() => [
        this.store.select(selectOperableExtendedBasketItems),
        this.store.select(selectActiveBasketCurrency),
        this.store.select(selectOperableBasketData),
        this.store.select(selectOperableReferencedProducts),
        ...defaultCollectorSelect(this.store),
      ]),
      switchMap(
        ([
          { eventMappingFunction, payload },
          basketItems,
          currency,
          basketData,
          referencedProducts,
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) =>
          this.mapToViewCartEvent([
            { eventMappingFunction, payload },
            basketItems,
            currency,
            basketData,
            referencedProducts,
            activeAccount as Account,
            presetDisplayName as string,
            user as IUser,
          ]),
      ),
    ),
  );

  public sendCheckoutBasketEventOnCheckBasketSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(gtmTrackActions.gtmCollectTrackData),
      filter((x) => x.name === EcommerceActionsToTrack.BeginCheckout),
      concatLatestFrom(() => [
        this.store.select(selectOperableExtendedBasketItems),
        this.store.select(selectActiveBasketCurrency),
        this.store.select(selectOperableBasketData),
        this.store.select(selectOperableReferencedProducts),
        this.store.select(selectAllBasketItemsTracking),
        ...defaultCollectorSelect(this.store),
      ]),
      concatMap(
        ([
          { eventMappingFunction, payload },
          basketItems,
          currency,
          basket,
          referencedProducts,
          basketItemsTracking,
          activeAccount,
          presetDisplayName,
          ,
          user,
        ]) => {
          const extendedItems = new Map();

          processNewItemsSize(basketItems, [], extendedItems);
          processNewItemsPrepack(
            basketItems,
            [],
            referencedProducts,
            extendedItems,
          );

          return this.ecommerceTrackingMapper
            .mapEventsFromAction({}, eventMappingFunction)
            .pipe(
              mergeMap((eventNames) =>
                eventNames.map((eventName) =>
                  gtmTrackActions.gtmSendTrackEvent({
                    name: eventName,
                    payload: {
                      ...payload,
                      currency,
                      basket,
                      referencedProducts,
                      extendedItems: [...extendedItems.values()],
                      basketItemsTracking,
                      activeAccount,
                      presetDisplayName,
                      user,
                    },
                  }),
                ),
              ),
            );
        },
      ),
    ),
  );

  private mapToViewCartEvent = ([
    { eventMappingFunction, payload },
    basketItems,
    currency,
    basketData,
    referencedProducts,
    activeAccount,
    presetDisplayName,
    user,
  ]: [
    {
      eventMappingFunction?: string;
      payload: IGtmProperties;
    },
    ExtendedBasketItem[],
    string | undefined,
    Basket | null,
    ReferencedProduct[] | undefined,
    Maybe<Account>,
    Maybe<string>,
    Maybe<IUser>,
  ]) => {
    const extendedItems = new Map();

    processNewItemsSize(basketItems, [], extendedItems);
    processNewItemsPrepack(basketItems, [], referencedProducts, extendedItems);

    return this.ecommerceTrackingMapper
      .mapEventsFromAction(payload, eventMappingFunction)
      .pipe(
        switchMap((eventNames) =>
          eventNames.map((eventName) =>
            gtmTrackActions.gtmSendTrackEvent({
              name: eventName,
              payload: {
                extendedItems: [...extendedItems.values()],
                currency,
                basketData,
                referencedProducts,
                activeAccount,
                presetDisplayName,
                user,
              },
            }),
          ),
        ),
      );
  };
}
